
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import GlobalViewModel from '@/state/GlobalViewModel';
import GlossRepository from '@/repositories/GlossRepository';
import GeneralScaffold from '@/components/GeneralScaffold.vue';
import { SearchContext } from './searchTypes';
import FormattedGlossName from '@/components/FormattedGlossName.vue';
import { BehaviorSubject, combineLatest, of as observableOf, Subscription, zip } from 'rxjs';
import Resource from '@/repositories/Resource';
import Gloss from '@/repositories/data/Gloss';
import Variant from '@/repositories/data/Variant';
import GlossVariantUsageExampleItem from '@/components/GlossVariantUsageExampleItem.vue';
import SignLocationImage from '@/components/SignLocationImage.vue';
import SignShapeImage from '@/components/SignShapeImage.vue';
import MediaFile from '@/repositories/data/MediaFile';
import UsageExample from '@/repositories/data/UsageExample';
import MediaFileVideoPlayer from '@/components/MediaFileVideoPlayer.vue';
import ContentWithOverlay from '@/components/ContentWithOverlay.vue';
import GesturePositionCard from '@/components/GesturePositionCard.vue';
import VariantCategory from '@/repositories/data/VariantCategory';
import { RawLocation, Route } from 'vue-router';
import GlossVariantCategorySelector from '@/components/GlossVariantCategorySelector.vue';
import SignDefinition from '@/repositories/data/SignDefinition';
import { distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import InformationBulb from '@/components/InformationBulb.vue';
import GrammarInfoHighlighter from '@/components/GrammarInfoHighlighter.vue';
import MediaFileThumbnail from '@/components/MediaFileThumbnail.vue';
import ExplanationItem from '@/components/ExplanationItem.vue';
import Explanation from '@/repositories/data/Explanation';
import GrammaticalInformation from '@/repositories/data/GrammaticalInformation';
import { mediaEndpoint } from '@/container';
import GlossSearchResult from '@/repositories/data/GlossSearchResult';
import MainSignDetails from '@/components/MainSignDetails.vue';

type ID = number | string;

type GlossSynonym = GlossSearchResult & {
    defaultFilm: NonNullable<GlossSearchResult['defaultFilm']>;
    defaultSignDefinition: SignDefinition;
};

type VariantCard =
    | { type: 'synonym'; gloss: GlossSynonym }
    | { type: 'signDefinition'; signDefinition: SignDefinition };

const variantCompare = (a: Variant, b: Variant) => {
    // Ignore if priority of a or b is not an int
    if (
        !a.category ||
        !b.category ||
        (!a.category.priority && a.category.priority !== 0) ||
        (!b.category.priority && b.category.priority !== 0)
    ) {
        return 0;
    }

    if (a.category.priority === b.category.priority) {
        return 0;
    }

    return a.category.priority < b.category.priority ? -1 : 1;
};

@Component({
    components: {
        MediaFileThumbnail,
        GlossVariantCategorySelector,
        SignShapeImage,
        SignLocationImage,
        GlossVariantUsageExampleItem,
        ExplanationItem,
        FormattedGlossName,
        GeneralScaffold,
        MediaFileVideoPlayer,
        ContentWithOverlay,
        GesturePositionCard,
        InformationBulb,
        GrammarInfoHighlighter,
        MainSignDetails,
    },
    beforeRouteLeave(to: Route, from: Route, next: (to?: any) => void): void {
        (this as GlossDetailsScreen).beforeRouteChanged(to, from, next);
    },
    beforeRouteUpdate(to: Route, from: Route, next: (to?: any) => void): void {
        (this as GlossDetailsScreen).beforeRouteChanged(to, from, next);
    },
})
export default class GlossDetailsScreen extends Vue {
    // Search context for prev/next navigation; null when opened without a search context
    @Prop({ default: null }) public searchContext!: SearchContext | null;
    // True while SearchContainer is still fetching results
    @Prop({ default: false }) public searchLoading!: boolean;

    public ALWAYS_SHOW_CATEGORIES = [
        // ASL
        'asl',
        'asl variant',
        // DGS
        'dgs',
        'dgs variant',
        // DTS
        'dts',
        // Straattaal
        'straattaal',
        'straattaal variant 1',
        'straattaal variant 2',
        // wingerd
        'wingerdgebaar',
        'wingerd synoniem',
        // van beek
        'van beek',
        // tactiele gebaren
        'tactiele gebaren',
        // Always show standaard
        'standaard',
    ];

    public gloss: Gloss | null = null;
    public fullscreenMedia: boolean = false;
    public loading: boolean = true;
    public activeExplanationFilm: MediaFile | null = null;
    public activeUsageFilm: MediaFile | null = null;
    public currentlyPlayingVideoId: string | null = null;
    public gestureCardVariantIds: string[] = [];
    public variantCardGestureIds: string[] = [];
    public lightboxImage: MediaFile | null = null;

    private moduleSubject = new BehaviorSubject<string | null>(null);
    private targetGlossIdSubject = new BehaviorSubject<string | null>(null);
    private glossSubscription = new Subscription();
    private amLoggedInSubject = new BehaviorSubject<boolean>(false);
    private amLoggedInSubscription = new Subscription();
    private variantCategoryId: ID | null = null;
    @Inject() private readonly globalViewModel!: GlobalViewModel;
    @Inject() private readonly glossRepository!: GlossRepository;

    public created(): void {
        this.updateFromRouteQuery(true);
        this.glossSubscription = combineLatest([
            this.globalViewModel.verifiedUser,
            this.targetGlossIdSubject,
            this.moduleSubject.pipe(distinctUntilChanged()),
        ])
            .pipe(
                switchMap(([user, id, moduleId]) =>
                    id === null
                        ? observableOf(Resource.loading<Gloss>(null))
                        : this.glossRepository.findGlossById(id, moduleId),
                ),
            )
            .subscribe(glossResource => {
                this.loading = glossResource.isLoading;
                if (glossResource.isSuccess && glossResource.hasData()) {
                    const gloss = glossResource.data;
                    if (gloss.variants) {
                        // sort variant by category id
                        gloss.variants.sort(variantCompare);
                    }
                    this.gloss = gloss;
                } else {
                    this.gloss = null;
                }
            });
        this.amLoggedInSubscription = this.globalViewModel.verifiedUser
            .pipe(map(user => user !== null))
            .subscribe(this.amLoggedInSubject);
        this.fetchData();
    }

    public beforeDestroy(): void {
        this.amLoggedInSubscription.unsubscribe();
        this.glossSubscription.unsubscribe();
    }

    get keywords(): string[] {
        return this.gloss !== null ? this.gloss.keywords.filter(term => term !== this.gloss?.name) : [];
    }

    get variant(): Variant | null {
        if (this.gloss === null || !this.gloss.variants || this.gloss.variants.length === 0) {
            return null;
        }

        // return variant with highest category priority
        if (this.variantCategoryId === null) {
            return this.gloss.variants[0];
        }

        return (
            this.gloss.variants.find(variant => {
                return typeof variant.category !== 'undefined' && variant.category.id === this.variantCategoryId;
            }) || null
        );
    }

    get otherVariants(): Variant[] {
        if (!this.gloss || !this.gloss.variants || this.gloss.variants.length === 0) {
            return [];
        }

        return this.gloss.variants.filter(v => v !== this.variant && v.showForSameGloss === true);
    }

    get lexiconFilm(): MediaFile | null {
        const variant = this.variant;
        if (variant && variant.signDefinitions && variant.signDefinitions.length > 0) {
            const signDefinition = variant.signDefinitions[0];
            if (signDefinition.film) {
                return signDefinition.film;
            }
        }
        return null;
    }

    get variantCategory(): VariantCategory | null {
        return this.variant && this.variant.category ? this.variant.category : null;
    }

    get menuSelectableVariantCategories(): VariantCategory[] {
        if (!this.gloss || !this.gloss.variants) {
            return [];
        }

        return this.gloss.variants
            .filter(v => {
                if (!v.category) return false;
                const categoryLabel = (v.category.label || '').trim().toLowerCase();

                // Always show these specific categories
                return this.ALWAYS_SHOW_CATEGORIES.includes(categoryLabel);
            })
            .map(v => v.category!);
    }

    get isSimpleExplanations(): boolean {
        return (
            (this.gloss &&
                this.gloss.explanations &&
                this.gloss.explanations.length > 0 &&
                !this.gloss.explanations.some(t => t.film)) ||
            false
        );
    }

    get isMediaExplanations(): boolean {
        return (
            (this.gloss &&
                this.gloss.explanations &&
                this.gloss.explanations.length > 0 &&
                this.gloss.explanations.some(t => t.film)) ||
            false
        );
    }

    get sortedExplanations(): Explanation[] {
        const explanations = (this.gloss && this.gloss.explanations && this.gloss.explanations) || [];
        const sorted = explanations
            .map((entry, index) => ({ index, priority: entry.priority ? entry.priority : 0 }))
            .sort((a, b) => a.priority - b.priority);
        return sorted.map(entry => explanations[entry.index]);
    }

    get sortedUsageExamples(): UsageExample[] {
        if (this.variant && this.variant.usageExamples) {
            const usageExamples = this.variant.usageExamples;
            const sorted = usageExamples
                .map((entry, index) => ({ index, priority: entry.priority ? entry.priority : 0 }))
                .sort((a, b) => a.priority - b.priority);
            return sorted.map(entry => usageExamples[entry.index]);
        }
        return [];
    }

    get seeAlsoVariants(): Variant[] {
        if (!this.variant || !this.variant.seeAlsoReferences || !this.variant.seeAlsoReferences.length) {
            return [];
        }

        return this.variant.seeAlsoReferences;
    }

    // Fixes the implicit 'any' TypeScript error and keeps template clean
    get grammaticalInfoText(): string {
        if (!this.variant || !this.variant.grammaticalInformation) {
            return '';
        }
        return this.variant.grammaticalInformation
            .filter((info: GrammaticalInformation) => !!info.text)
            .map((info: GrammaticalInformation) => info.text)
            .join('\n\n');
    }

    get variantCards(): VariantCard[] {
        const cards: VariantCard[] = [];

        // Synonyms first
        if (this.gloss && this.gloss.synonyms) {
            this.gloss.synonyms
                .filter(s => !!s.defaultFilm)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach(fullSynonym => {
                    const { variants, ...synonym } = fullSynonym;
                    const signDefinitions = variants.flatMap(v => v.signDefinitions!);
                    const defaultSignDefinition =
                        signDefinitions.find(d => d.film?.id === synonym.defaultFilm?.id) || null;
                    cards.push({
                        type: 'synonym',
                        gloss: {
                            ...synonym,
                            defaultSignDefinition,
                        } as GlossSynonym,
                    });
                });
        }

        // Extra sign definitions (index > 0) from the current variant
        if (this.variant && this.variant.signDefinitions && this.variant.signDefinitions.length > 1) {
            this.variant.signDefinitions
                .slice(1)
                .forEach(sd => cards.push({ type: 'signDefinition', signDefinition: sd }));
        }

        return cards;
    }

    get firstVariantCard(): VariantCard | null {
        return this.variantCards[0] || null;
    }

    get remainingVariantCards(): VariantCard[] {
        return this.variantCards.slice(1);
    }

    get playingFirstVariantCard(): boolean {
        return (
            this.firstVariantCard !== null &&
            this.firstVariantCard.type === 'synonym' &&
            this.firstVariantCard.gloss.defaultFilm?.id?.toString() === this.currentlyPlayingVideoId
        );
    }

    @Watch('gloss')
    public onGlossChange(gloss: Gloss | null): void {
        const explanationsWithFilm = gloss && gloss.explanations ? gloss.explanations.filter(t => t.film) : [];
        if (explanationsWithFilm.length) {
            const [firstExplanationWithFilm] = explanationsWithFilm;
            this.activeExplanationFilm =
                firstExplanationWithFilm && firstExplanationWithFilm.film ? firstExplanationWithFilm.film : null;
        } else {
            this.activeExplanationFilm = null;
        }
    }

    @Watch('variant')
    public onVariantChange(variant: Variant | null): void {
        const examplesWithFilm =
            variant && variant.usageExamples ? variant.usageExamples.filter(ue => ue.phrase && ue.phrase.film) : [];
        if (examplesWithFilm.length) {
            const [firstExampleWithFilm] = examplesWithFilm;
            // ugly code because TS is stupid
            this.activeUsageFilm =
                firstExampleWithFilm && firstExampleWithFilm.phrase && firstExampleWithFilm.phrase.film
                    ? firstExampleWithFilm.phrase.film
                    : null;
        } else {
            this.activeUsageFilm = null;
        }
        const dataLayer = (window as any).dataLayer || [];
        dataLayer.push({
            event: 'gloss_view',
            gloss_id: variant && this.gloss ? this.gloss.id : null,
            gloss_name: variant && this.gloss ? this.gloss.name : null,
            variant_id: variant ? variant.id : null,
            variant_category: variant && variant.category ? variant.category.label : null,
        });
    }

    public getMediaImageUrlForGloss(media: MediaFile): string {
        return `${mediaEndpoint}/media/img/${media.id}?gloss=${this.gloss ? this.gloss.id : ''}`;
    }

    public onExplanationSelect(explanation: Explanation): void {
        if (explanation.film) {
            this.activeExplanationFilm = explanation.film;
            this.currentlyPlayingVideoId = explanation.film.id || null;
        }
    }

    public onUsageExampleSelect(example: UsageExample): void {
        if (example.phrase && example.phrase.film) {
            this.activeUsageFilm = example.phrase.film;
            this.currentlyPlayingVideoId = example.phrase.film.id || null;
        }
    }

    public onReferencedGlossVariantSelected(variant: Variant): void {
        if (variant.gloss && variant.gloss.id && variant.category && variant.category.id) {
            this.$router.push({
                name: 'gloss-details',
                params: { id: variant.gloss.id, category_id: variant.category.id },
            } as RawLocation);
        }
    }

    public onVariantSelect(category: VariantCategory): void {
        if (this.gloss && this.gloss.id) {
            this.$router.push({
                name: 'gloss-details',
                params: { id: this.gloss.id, category_id: category.id },
            } as RawLocation);
        }
    }

    public isShowingGestureCard(variantId: string): boolean {
        return this.gestureCardVariantIds.includes(variantId);
    }

    public toggleGestureCard(variantId: string): void {
        const idx = this.gestureCardVariantIds.indexOf(variantId);
        if (idx > -1) {
            this.gestureCardVariantIds.splice(idx, 1);
        } else {
            this.gestureCardVariantIds.push(variantId);
        }
    }

    public isShowingVariantCardGesture(key: string): boolean {
        return this.variantCardGestureIds.includes(key);
    }

    public toggleVariantCardGesture(key: string): void {
        const idx = this.variantCardGestureIds.indexOf(key);
        if (idx > -1) {
            this.variantCardGestureIds.splice(idx, 1);
        } else {
            this.variantCardGestureIds.push(key);
        }
    }

    public openImageLightbox(media: MediaFile): void {
        this.lightboxImage = media;
    }

    public closeLightbox(): void {
        this.lightboxImage = null;
    }

    @Watch('$route')
    public onRouteChange(to: Route): void {
        this.updateFromRouteQuery(false);
        const currentGlossId = this.gloss && this.gloss.id ? this.gloss.id : null;
        const targetGlossId = to.params.id || null;

        this.variantCategoryId = this.$route.params.category_id || null;

        if (currentGlossId !== targetGlossId) {
            this.fetchData();
        }
    }

    public fetchData(): void {
        const id = this.$route.params.id || null;
        this.targetGlossIdSubject.next(id);
    }

    get canGoPrev(): boolean {
        if (!this.searchContext) return false;
        return this.searchContext.currentIndex > 0 || this.searchContext.currentPage > 1;
    }

    get canGoNext(): boolean {
        if (!this.searchContext) return false;
        return (
            this.searchContext.currentIndex < this.searchContext.results.length - 1 ||
            this.searchContext.currentPage < this.searchContext.totalPageCount
        );
    }

    public onBack(): void {
        this.$router.go(-1);
    }

    public onBackToSearch(): void {
        this.$router.push({ name: 'search', query: { ...this.$route.query } }).catch(() => {});
    }

    public onPrev(): void {
        if (!this.searchContext || !this.canGoPrev) return;
        if (this.searchContext.currentIndex > 0) {
            // Navigate within the same page
            const newIndex = this.searchContext.currentIndex - 1;
            const gloss = this.searchContext.results[newIndex];
            this.$emit('navigate-to-gloss', gloss.id, newIndex);
        } else {
            // At the first item of this page → load the previous page and select its last item
            this.$emit('navigate-to-prev-page');
        }
    }

    public onNext(): void {
        if (!this.searchContext || !this.canGoNext) return;
        if (this.searchContext.currentIndex < this.searchContext.results.length - 1) {
            // Navigate within the same page
            const newIndex = this.searchContext.currentIndex + 1;
            const gloss = this.searchContext.results[newIndex];
            this.$emit('navigate-to-gloss', gloss.id, newIndex);
        } else {
            // At the last item of this page → load the next page and select its first item
            this.$emit('navigate-to-next-page');
        }
    }

    public onSearchSubmit(search: string | null): void {
        if (search) {
            this.$router.push({ name: 'search', query: { q: search } });
        }
    }

    public onToggleFavoured() {
        if (this.gloss) {
            this.gloss.favoured = !this.gloss.favoured;
            // We actually don't care to much about the result, we can't do much about it anyway
            zip(
                this.globalViewModel.verifiedUser.pipe(first()),
                observableOf(this.gloss.id),
                observableOf(this.gloss.favoured),
            )
                .pipe(switchMap(([user, id, favourite]) => this.glossRepository.setAsFavourite(id, favourite)))
                .subscribe();
        }
    }

    private updateFromRouteQuery(initial: boolean): void {
        if (this.$route.query.hasOwnProperty('l')) {
            this.moduleSubject.next(typeof this.$route.query.l === 'string' ? this.$route.query.l : null);
        }
        if (initial) {
            this.variantCategoryId = this.$route.params.category_id || null;
        }
    }

    // noinspection JSUnusedLocalSymbols
    private beforeRouteChanged(to: Route, from: Route, next: (to?: any) => void): void {
        // @ts-ignore
        const back = window.popStateDetected;
        if (back && this.fullscreenMedia) {
            this.fullscreenMedia = false;
            next(false);
        } else {
            next();
        }
    }
}
